import { Box, useLayoutContext } from "arkh";
import React, { useMemo } from "react";
import {
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  View,
  useWindowDimensions,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Animated, { Easing, FadeIn } from "react-native-reanimated";
import { useSizeClass } from "../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { CardEnteringAnimation } from "./Popover";
import { Portal } from "@gorhom/portal";
import { FullWindowOverlay } from "react-native-screens";
export const AbsolutePopoverHostName = "AbsolutePopover-host";
function P(props: { children: React.ReactNode }) {
  return <Portal hostName={AbsolutePopoverHostName}>{props.children}</Portal>;
}
function WebFullScreen(props: {
  children: React.ReactNode;
  onClose?: () => void;
}) {
  return (
    <Portal hostName={AbsolutePopoverHostName}>
      <Modal
        visible
        transparent
        presentationStyle="overFullScreen"
        animationType="fade"
        onRequestClose={props.onClose}
      >
        {props.children}
      </Modal>
    </Portal>
  );
}

export const FullScreen = Platform.select({
  web: WebFullScreen,
  android: P,
  default: P,
  ios: FullWindowOverlay as any,
});

type AbsolutePopoverProps = {
  children: React.ReactNode;
  left: number;
  top: number;
  buttonWidth: number;
  onClose: () => void;
  popoverWidth?: number;
};
export function AbsolutePopover(props: AbsolutePopoverProps) {
  const dimensions = useWindowDimensions();
  const side = useMemo(
    () =>
      props.left + props.buttonWidth / 2 > dimensions.width / 2
        ? "left"
        : "right",
    [props.left, props.buttonWidth, dimensions.width]
  );
  const verticalPosition = useMemo(
    () => (props.top < dimensions.height / 2 ? "top" : "bottom"),
    [props.top, dimensions.height]
  );
  const layout = useLayoutContext();
  const safeAreaInsets = useSafeAreaInsets();
  const isCompact = useSizeClass().width === SizeClass.COMPACT;

  const webOnClose =
    Platform.OS === "web"
      ? {
          onClose: props.onClose,
        }
      : {};

  const popoverWidth = useMemo(
    () =>
      props.popoverWidth !== undefined
        ? props.popoverWidth
        : Platform.OS === "web" && !isCompact
          ? 300
          : 250,
    [props.popoverWidth, isCompact]
  );

  return (
    <FullScreen {...webOnClose}>
      <View
        style={
          Platform.OS === "android"
            ? StyleSheet.absoluteFill
            : {
                position: "absolute",
                height: dimensions.height,
                width: dimensions.width,
              }
        }
      >
        <View
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            zIndex: 1000,
            width: "100%",
            height: "100%",
            justifyContent: "flex-start",
          }}
        >
          <Animated.View
            entering={
              Platform.OS === "web"
                ? undefined
                : FadeIn.duration(100)
                    .easing(Easing.in(Easing.linear))
                    .withInitialValues({ opacity: 0 })
            }
            style={[
              {
                width: "100%",
                height: "100%",
                top: 0,
                bottom: 0,
                right: 0,
                left: 0,
                position: "absolute",
                backgroundColor: isCompact
                  ? "rgba(0,0,0,0.3)"
                  : "rgba(0,0,0,0.05)",
                zIndex: -1,
              },
            ]}
          >
            <Pressable
              onPress={() => {
                props.onClose();
              }}
              style={{
                flex: 1,
              }}
            />
          </Animated.View>
          <Animated.View
            entering={Platform.OS === "web" ? undefined : CardEnteringAnimation}
            style={{
              position: "absolute",
              maxHeight: "90%",
              width: popoverWidth,
              top: verticalPosition === "top" ? props.top : undefined,
              bottom:
                verticalPosition === "bottom"
                  ? dimensions.height - props.top
                  : undefined,
              left:
                props.left -
                (side === "left" ? popoverWidth - props.buttonWidth : 0),
              paddingBottom:
                verticalPosition === "top"
                  ? safeAreaInsets.bottom + layout.space.medium
                  : undefined,
              paddingTop:
                verticalPosition === "bottom"
                  ? safeAreaInsets.top + layout.space.medium
                  : undefined,
            }}
          >
            <Box
              style={{
                overflow: "hidden",
              }}
              cornerRadius="large"
              color="surface-primary"
            >
              {props.children}
            </Box>
          </Animated.View>
        </View>
      </View>
    </FullScreen>
  );
}
